import * as React from 'react'
import { Link } from 'react-router-dom'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'outline' | 'danger' | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'lg'

export const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md font-semibold cursor-pointer transition-all duration-200 ease-out active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:opacity-60 disabled:cursor-default disabled:active:scale-100',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-contrast border border-transparent hover:bg-accent-hover',
        outline: 'bg-surface text-text-2 border border-border-input hover:text-accent hover:border-accent',
        danger:  'bg-surface text-danger-text border border-danger-border hover:bg-danger-bg',
        ghost:   'bg-transparent text-text-2 border border-transparent font-medium hover:text-accent hover:bg-accent-muted',
      },
      size: {
        sm: 'px-2 py-1 text-xs',
        md: 'px-5 py-[0.6rem] text-sm',
        lg: 'px-6 py-[0.7rem] text-[0.95rem]',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export function Button({ variant, size, className, ...rest }: ButtonProps) {
  return (
    <button
      className={cn(buttonVariants({ variant, size }), className)}
      {...rest}
    />
  )
}

interface ButtonLinkProps extends VariantProps<typeof buttonVariants> {
  to: string
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
}

export function ButtonLink({ to, variant, size, className, style, children }: ButtonLinkProps) {
  return (
    <Link
      to={to}
      style={style}
      className={cn(buttonVariants({ variant, size }), 'no-underline', className)}
    >
      {children}
    </Link>
  )
}
